import type {
  StoreStockTrackingCandidatesListQuery,
  StoreStockTrackingListQuery
} from "@repo/api-sdk";
import type { HttpTypes } from "@medusajs/types";

import { queryKeysFactory } from "../queryKeyFactory";

const ADDRESSES_QUERY_KEY = "customer-addresses" as const;
export const addressesQueryKeys = {
  ...queryKeysFactory<typeof ADDRESSES_QUERY_KEY, HttpTypes.SelectParams>(
    ADDRESSES_QUERY_KEY
  )
};

const CUSTOMERS_QUERY_KEY = "customers" as const;
export const customersQueryKeys = {
  // ...queryKeysFactory(CUSTOMERS_QUERY_KEY),
  profile: () => [CUSTOMERS_QUERY_KEY, "profile"] as const
};

const FAVORITES_QUERY_KEY = "favorites" as const;
export const favoritesQueryKeys = {
  /** One global membership set - no per-item detail key needed. */
  all: () => [FAVORITES_QUERY_KEY] as const
};

const SAVED_LISTS_QUERY_KEY = "saved-lists" as const;
export const savedListsQueryKeys = {
  ...queryKeysFactory<typeof SAVED_LISTS_QUERY_KEY>(SAVED_LISTS_QUERY_KEY),
  /**
   * Live dry-run resolve of a list against current offers - a distinct key from
   * `detail` (the stored rows) so the two caches don't clobber each other.
   */
  preview: (id: string) => [SAVED_LISTS_QUERY_KEY, "preview", id] as const
};

const SUPPLIER_MATCH_QUERY_KEY = "supplier-match" as const;
export const supplierMatchQueryKeys = {
  ...queryKeysFactory<typeof SUPPLIER_MATCH_QUERY_KEY>(
    SUPPLIER_MATCH_QUERY_KEY
  ),
  /**
   * A seller's Case B email state (GET/POST/DELETE /store/sellers/{id}/
   * supplier-match) - a distinct branch from the grid `list`, so patching one
   * card's dialog state never clobbers the other.
   */
  sellerState: (sellerId: string) =>
    [SUPPLIER_MATCH_QUERY_KEY, "seller-state", sellerId] as const
};

const STOCK_TRACKING_QUERY_KEY = "stock-tracking" as const;
export const stockTrackingQueryKeys = {
  ...queryKeysFactory<
    typeof STOCK_TRACKING_QUERY_KEY,
    StoreStockTrackingListQuery
  >(STOCK_TRACKING_QUERY_KEY),
  /**
   * Seed candidates from order history - a distinct branch from the tracked
   * `list` so the two caches never clobber each other. Call with no arg for the
   * invalidation prefix; with a query for a specific page.
   */
  candidates: (query?: StoreStockTrackingCandidatesListQuery) =>
    [
      STOCK_TRACKING_QUERY_KEY,
      "candidates",
      ...(query ? [{ query }] : [])
    ] as const,
  /**
   * Live dry-run resolve of tracked variants against current offers (api#152) -
   * the "from <supplier> - $X" a due row shows. A distinct branch from `list`
   * (the stored rows), keyed by the sorted variant set so any id combination
   * (one due item, or all due) caches independently. Call with no arg for the
   * invalidation prefix.
   */
  reorderPreview: (variantIds?: string[]) =>
    [
      STOCK_TRACKING_QUERY_KEY,
      "reorder-preview",
      ...(variantIds ? [[...variantIds].sort()] : [])
    ] as const
};
